import BlogPage from 'src/views/Marketing/Blog/components/BlogPage';
import Footer from 'src/views/Marketing/components/Footer';
import NavBar from 'src/views/Marketing/components/NavBar';
import { ScrollView, StyleSheet } from 'react-native';

export default function Page() {
  return (
    <ScrollView style={styles.scrollView}>
      <NavBar />
      <BlogPage
        author='OneShop Team'
        dateTime='2022-03-29'
        tag='eBay'
        title='Sell with confidence with eBay Authenticity Guarantee – and keep those fraud claims at bay!'
      >
        <p>
          Online marketplaces are not impermissible to counterfeit items and their different degrees of imitation, from
          Class A to Class AAA. They may be called reproductions or replicas, but the bottom line is these are fake
          items masquerading as original, branded, or designer items meant to deceive buyers.
        </p>

        <p>
          Counterfeit items also pose a risk to the business on the seller's side. Some buyers may file a refund claim
          for supposedly receiving a fake product. They would then get their refund but send back a different item – a
          real fake item this time.
        </p>

        <p>
          eBay has a solution to this. They have an authentication system called eBay Authenticity Guarantee that
          ensures buyers only receive genuine items and that sellers get their original goods back. Their pool of expert
          in-house and third-party authenticators subject the eligible pieces to a meticulous multiple-point physical
          check and inspection using modern specialized equipment.
        </p>

        <p>
          At the moment, this program is limited to items under specific categories, brands, and selling price points
          and is available only for buyers with delivery addresses within the US and certain US territories.
        </p>

        <h3>How does Authenticity Guarantee work?</h3>

        <p>
          Suppose your listed sneakers, handbag, watch, or trading card meet the requirements for the category it
          belongs in (to be discussed below). In that case, a blue checkmark badge will appear, indicating its
          eligibility for the program.
        </p>

        <p>
          Once a buyer purchases this item, you have to ship this to the nearest authentication facility, the address of
          which will be given by eBay. If it passes a thorough inspection, the item will be tagged with an
          authentication card or a tamper-proof sticker before it gets dispatched to the buyer within 2 to 4 days. Note
          that the authentication service, including the shipping to the facility, is free of charge.{' '}
        </p>

        <p>
          The same process will be followed when a buyer claims that an item is fake and demands a refund (only
          applicable if the seller accepts returns as stated in their policy). The buyer must ship the item to the
          facility first and not directly to the seller. The experts will then check for the NFC tag or QR-code sticker,
          which signifies that the item was returned to the facility in the same condition the buyer received it.
        </p>

        <p>
          If found to be a counterfeit, the authenticating body will return the item to the seller, who will shoulder
          the shipping and, at the same time, will refund the buyer. Otherwise, there will be no refunds, and the
          authenticating party will send the item back to the buyer.
        </p>

        <h3>Useful Tips</h3>

        <p>For a smooth authentication process, consider these general tips:</p>

        <ul>
          <li>
            Craft honest and accurate listing descriptions and include product images that match what the buyer
            receives. Disclose any imperfections and inclusions. If the authenticators cannot inspect the items due to
            inconsistencies between the information in the listing and the actual items, they would ship them back at
            cost to the seller and refund the buyer.
          </li>
          <li>
            Avoid listing items that have been altered, modified, and personalized (such as with engraved initials).
            This is another reason authenticators cannot perform the procedure and send the goods back to the seller.
          </li>
          <li>
            Don't include accessories and freebies that don't originally come with the branded or designer item you're
            listing. The authenticators will remove these and only send the authenticated item to the buyer.
          </li>
          <li>
            If you list multiple goods for authentication, send the items to the facility individually. Promo or
            combined shipping doesn't work with this service.
          </li>
        </ul>

        <h3>What items are eligible for Authenticity Guarantee? </h3>

        <p>
          As of this writing, the eligible items belong to these four categories, both for women and men: Athletic shoes
          (sneakers), watches, handbags, and trading cards. The verification process is largely similar among these
          groups, only that experts look for a unique set of authenticity indicators per category. Qualifiers such as
          selling price and other specifications may also vary. Let us go over the significant points for each one:
        </p>

        <ul>
          <li>
            <h4>Sneakers</h4>
            <p>
              <strong>Points for inspection:</strong> Sizing label, logo, laces, heel tabs, box (if the declared item
              condition is "New with box")
            </p>
            <p>
              <strong>Brands accepted at the moment:</strong> Off-White, Yeezy, Adidas, Jordan, Louis Vuitton, New
              Balance, Nike, to name some of the popular eligible labels.
            </p>
            <p>
              <strong>Selling price:</strong> At least $100 if the pair is in new condition; $150 or more if it's
              pre-owned. These amounts apply to items listed with either a "Buy It Now" price or are in an auction-style
              format.
            </p>
            <p>
              <strong>Note:</strong> Signed sneakers are not be accepted.
            </p>
          </li>

          <li>
            <h4>Watches</h4>
            <p>
              <strong>Points for inspection:</strong> Serial number, case, end links, hardware (buckle or clasp), crown,
              dial, bezel, and hands.
            </p>
            <p>
              <strong>Brands accepted at the moment:</strong> Any brand of timepiece listed under the primary
              wristwatches category with a "Buy it Now" price or a starting bidding price of $2,000 is accepted. Premium
              models from high-end brands like Omega, Rolex, and Breitling are automatically eligible regardless of the
              listing price.{' '}
            </p>
            <p>
              Vintage watches are eligible if verified as authentic by a third-party eBay authentication partner. Due to
              the age of the timepieces, off-brand replacement parts are accepted if the original brand no longer
              produces these components.
            </p>
            <p>
              <strong>Note:</strong> Modifications to the vintage watches are accepted if only the commonly replaced
              parts are involved, such as the strap, bracelet, gasket, and crystal. Customized details like a painted
              dial or engraved initials on the case will render the timepiece ineligible for the program.
            </p>
          </li>

          <li>
            <h4>Handbags</h4>
            <p>
              <strong>Points for inspection:</strong> Packaging, smell, accessories, and details such as the strap and
              hardware
            </p>
            <p>
              <strong>Brands accepted at the moment:</strong> Eligible brands include Chanel, Hermes, Celine, Dior,
              Prada, Louis Vuitton, YSL, Fendi, Burberry, Goyard, and Chloe, to name some.
            </p>
            <p>
              <strong>Selling price:</strong> At least $500, either the item is listed upfront or with a starting bid
              price. It can be brand new (with or without the tag) or pre-owned. A signature upon delivery to the buyer
              is required.
            </p>
          </li>

          <li>
            <h4>Trading Cards</h4>
            <p>
              <strong>Points for inspection:</strong> The third-party authenticators authorized by eBay will thoroughly
              check the card's corners, edges, surface, and condition for any inconsistencies and signs of authenticity.
            </p>
            <p>Cards accepted at the moment:</p>
            <ul>
              <li>Ungraded sports trading cards</li>
              <li>Collectible card games</li>
              <li>
                Non-sports trading cards with a selling price of at least $500 will require a signature upon delivery.
              </li>
            </ul>
            <p>
              <strong>Note:</strong> Autographed cards are not eligible, and no grade evaluation will be issued for the
              authenticated card. The objective of the program is only to verify the card as genuine.
            </p>
          </li>
        </ul>

        <p>
          eBay's Authenticity Guarantee is a great program that will lend credibility to the high-value items you're
          selling with no additional cost to you and protect you from fraudulent buyer claims.
        </p>

        <p>
          <strong>Here's another good deal:</strong> OneShop, an all-in-one app that lets you cross-list from eBay to
          other reselling platforms like Poshmark and Mercari. Sign up now for the free 7-day trial to experience the
          benefits of cross-listing and other features.{' '}
        </p>
      </BlogPage>
      <Footer />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
});
